import { Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SideNav } from "../components/layout/SideNav";
import { TopBar } from "../components/layout/TopBar";
import { useAppStore } from "../stores/appStore";
import { useAuthStore } from "../stores/authStore";
import { FullPageLoader } from "../components/ui/LoadingSpinner";
import { useLocation } from "react-router-dom";

export function AppLayout() {
  const { sidebarCollapsed } = useAppStore();
  const { isLoading, user } = useAuthStore();
  const location = useLocation();

  if (isLoading && !user) {
    return <FullPageLoader label="Initializing..." />;
  }

  // App shell uses a fixed-height viewport with an internal scroll region in
  // <main>. Body scroll was unreliable on some pages (Dashboard, Assets) —
  // likely from the combination of `body { overflow-x: hidden }` (which
  // implicitly makes body a scroll container on Y) and the framer-motion
  // <motion.div> wrapping each page. Pinning the shell to h-screen and giving
  // <main> overflow-y-auto removes the dependency on body scroll entirely.
  // Side effect: viewer pages that use h-[calc(100vh-4rem)] still fill the
  // visible main area exactly because TopBar is 4rem.
  return (
    <div className="h-screen overflow-hidden">
      <SideNav />
      <motion.div
        initial={false}
        animate={{ marginLeft: sidebarCollapsed ? 72 : 256 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className="flex h-screen flex-col"
      >
        <TopBar />
        <main className="relative flex-1 overflow-y-auto p-4 lg:p-6">
          {/* Gradient border glow on top — sits at the top of main's
              coordinate space and scrolls away with the content (matches
              previous behavior; main is the scroll container now). */}
          <div className="pointer-events-none absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-brand-500/30 to-transparent" />
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </motion.div>
    </div>
  );
}
